import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { BOM_RULE_LABELS, BOM_RULE_TYPES } from '@erp/shared';
import { api, errorMessage } from '@/lib/api';
import { FloatingPanel } from '@/components/ui/floating-panel';
import { EditLockBanner } from '@/components/ui/edit-lock-banner';
import { useEditLock } from '@/hooks/useEditLock';
import { Button } from '@/components/ui/button';
import { Field, Input, Select } from '@/components/ui/input';
import {
  bomLineToForm, buildBomLinePatch, papelFormula, validateBomLineForm, type BomLineForm
} from './bom-line-patch';

export interface EditableBomLine {
  id: string;
  productId: string;
  sku: string;
  name: string;
  ruleType: string;
  fixedQty: string | number;
  formula: string | null;
  wastePct: string | number;
  isOptional: boolean;
  optionCode: string | null;
  notes: string | null;
}

/**
 * Editar una línea de la BOM sin salir del modelo.
 *
 * Solo se abre sobre una versión **en borrador**: una BOM activa está referenciada por pedidos y
 * kits, así que se edita sobre una copia nueva (el botón «Nueva versión» de la pestaña).
 */
export function BomLineEditor({ line, open, onClose, onSaved, options, versionId }: {
  line: EditableBomLine | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  options: { code: string; name: string }[];
  /** Versión de la BOM: el bloqueo es de la versión, no de la línea, para que no se editen dos. */
  versionId?: string | null;
}) {
  const bloqueo = useEditLock('bom_version', open && versionId ? versionId : null);
  const soloLectura = !bloqueo.canEdit;
  const [form, setForm] = useState<BomLineForm>({ ...bomLineToForm({
    productId: '', ruleType: 'fixed', fixedQty: 1, formula: null, wastePct: 0,
    isOptional: false, optionCode: null, notes: null
  }) });
  const [saving, setSaving] = useState(false);

  // Al abrir otra línea se recarga el formulario: si no, los cambios de una se aplicarían a la otra.
  useEffect(() => {
    if (open && line) setForm(bomLineToForm(line));
  }, [open, line]);

  const set = (campo: keyof BomLineForm, valor: string | boolean) =>
    setForm((previo) => ({ ...previo, [campo]: valor }));
  const papel = papelFormula(form.ruleType);

  const submit = async () => {
    if (!line) return;
    const problema = validateBomLineForm(form);
    if (problema) { toast.error(problema); return; }
    setSaving(true);
    try {
      await api(`/bom-lines/${line.id}`, { method: 'PATCH', body: buildBomLinePatch(form) });
      toast.success('Línea actualizada');
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FloatingPanel
      open={open} onClose={onClose}
      title={line ? `Editar ${line.sku}` : 'Editar componente'}
      description={line?.name}
      footer={<><Button variant="outline" onClick={onClose}>Volver</Button>
        <Button loading={saving} disabled={soloLectura} onClick={() => void submit()}>Guardar</Button></>}
    >
      <div className="space-y-3">
        <EditLockBanner
          state={bloqueo.state}
          onTakeOver={() => void bloqueo.takeOver()}
          onRetry={() => void bloqueo.retry()}
        />
        <fieldset disabled={soloLectura} className="m-0 min-w-0 space-y-3 border-0 p-0">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Regla de cálculo">
            <Select aria-label="Regla de cálculo" value={form.ruleType} onChange={(e) => set('ruleType', e.target.value)}>
              {BOM_RULE_TYPES.map((rule) => <option key={rule} value={rule}>{BOM_RULE_LABELS[rule]}</option>)}
            </Select>
          </Field>
          <Field label="Merma (%)">
            <Input aria-label="Merma" type="number" step="0.01" min="0" max="100" value={form.wastePct}
              onChange={(e) => set('wastePct', e.target.value)} />
          </Field>
        </div>

        {form.ruleType === 'fixed' ? (
          <Field label="Cantidad fija" required>
            <Input aria-label="Cantidad fija" type="number" step="0.001" min="0" value={form.fixedQty}
              onChange={(e) => set('fixedQty', e.target.value)} />
          </Field>
        ) : papel === 'no-usada' ? (
          <p className="rounded-md border border-border bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground">
            {form.ruleType === 'power_selector'
              ? 'Esta regla pide una unidad por espejo según la potencia: no lleva fórmula.'
              : form.ruleType === 'area'
                ? 'Esta regla calcula la superficie (ancho × alto): no lleva fórmula.'
                : 'Esta regla calcula el perímetro (2 × (ancho + alto)): no lleva fórmula.'}
          </p>
        ) : (
          <Field
            label="Fórmula"
            required={papel === 'requerida'}
            hint={papel === 'requerida'
              ? 'Variables: width, height, perimeter, area, power, qty'
              : 'Opcional. Vacía usa el perímetro, que es lo normal en una tira de LED.'}
          >
            <Input aria-label="Fórmula" value={form.formula} className="font-mono" maxLength={200}
              onChange={(e) => set('formula', e.target.value)} placeholder="perimeter * 1.05" />
          </Field>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <label className="flex items-center gap-2 pb-2 text-xs">
            <input type="checkbox" checked={form.isOptional} onChange={(e) => set('isOptional', e.target.checked)} />
            Solo si la opción está activa
          </label>
          {form.isOptional ? (
            <Field label="Opción" className="flex-1">
              <Select aria-label="Opción" value={form.optionCode} onChange={(e) => set('optionCode', e.target.value)}>
                <option value="">Selecciona…</option>
                {options.map((option) => <option key={option.code} value={option.code}>{option.name}</option>)}
                {form.optionCode && !options.some((option) => option.code === form.optionCode) ? (
                  <option value={form.optionCode}>{form.optionCode} (ya no existe)</option>
                ) : null}
              </Select>
            </Field>
          ) : null}
        </div>

        <Field label="Notas" hint="Para quien monte la BOM después: por qué está esta pieza aquí.">
          <Input aria-label="Notas" value={form.notes} maxLength={500} onChange={(e) => set('notes', e.target.value)} />
        </Field>
        </fieldset>
      </div>
    </FloatingPanel>
  );
}
